// Latest-only, debounced runner for the stats breakdown, which scans raw rows
// server-side and must not pile up while the user drags through dates.
//
//   - schedule(task) debounces: only the last task scheduled within
//     `debounceMs` fires, and nothing pending starts before its quiet period
//     has passed;
//   - one task runs at a time. When the quiet period ends, a newer task aborts
//     the running one (its AbortSignal fires and fetch drops the request — the
//     server's scan still runs to completion or its 15 s statement_timeout)
//     and starts once it has settled; tasks superseded while waiting are
//     dropped without ever running;
//   - cancel() drops everything and aborts the running task (unmount).
//
// A task is `(signal, isCurrent) => Promise`. It must publish nothing unless
// `isCurrent()`: that turns false the moment a newer task is SCHEDULED (or on
// cancel), not only when the task is aborted — a task that finishes inside its
// replacement's debounce window is never aborted, yet its answer is already
// for the wrong range.

export const createLatestRunner = ({ debounceMs = 400 } = {}) => {
  let timer = null;
  let running = null; // AbortController of the running task
  let next = null;
  // Bumped by every schedule() and cancel(); a task is current while the
  // generation it was scheduled in is the latest.
  let generation = 0;

  // Starts the pending task only when nothing runs AND no debounce window is
  // open: a settling task must not pull in work whose quiet period has not
  // passed yet.
  const start = () => {
    if (running || timer !== null || !next) return;
    const { task, gen } = next;
    next = null;
    const controller = new AbortController();
    const isCurrent = () => gen === generation && !controller.signal.aborted;
    running = controller;
    Promise.resolve()
      .then(() => task(controller.signal, isCurrent))
      .catch(() => undefined)
      .finally(() => {
        running = null;
        start();
      });
  };

  const fire = () => {
    timer = null;
    running?.abort();
    start();
  };

  return {
    schedule: (task) => {
      generation += 1;
      next = { task, gen: generation };
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(fire, debounceMs);
    },
    cancel: () => {
      generation += 1;
      if (timer !== null) clearTimeout(timer);
      timer = null;
      next = null;
      running?.abort();
    },
    /** For tests: is a task running right now? */
    busy: () => running !== null,
  };
};
